import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { MessageSquareText, Lightbulb } from 'lucide-react';

export const QUESTION_TYPE_LABEL = {
  scenario: 'Scenario', short_answer: 'Short Answer', multi_select: 'Select All', ordering: 'Order', multiple_choice: 'Choice',
};

export function ScenarioBox({ scenario, compact }) {
  if (!scenario) return null;
  return (
    <div className={`rounded-xl border border-[#1B2A4A]/15 bg-[#1B2A4A]/[0.04] ${compact ? 'p-3 mb-3' : 'p-4 mb-4'}`} data-testid="scenario-box">
      <div className="flex items-center gap-1.5 mb-1"><MessageSquareText className="w-3.5 h-3.5 text-[#D9480F]" /><span className="text-[10px] uppercase tracking-wider font-semibold text-[#D9480F]">On the floor</span></div>
      <p className="text-sm text-stone-700 leading-relaxed italic">{scenario}</p>
    </div>
  );
}

export function ChoiceOptions({ options, value, onChange, questionIdx, compact }) {
  return (
    <div className={compact ? 'space-y-2' : 'space-y-3'}>
      {options?.map((option, optIdx) => {
        const selected = value === optIdx;
        return (
          <button key={optIdx} onClick={() => onChange(optIdx)}
            className={`w-full text-left ${compact ? 'p-3 rounded-lg' : 'p-4 rounded-xl'} border-2 transition-all text-sm font-medium ${selected ? 'border-[#1B2A4A] bg-[#1B2A4A]/5 text-stone-900' : 'border-stone-200 hover:border-stone-300 text-stone-700 bg-white'}`}
            data-testid={`answer-option-${questionIdx}-${optIdx}`}>
            <div className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${selected ? 'border-[#1B2A4A] bg-[#1B2A4A]' : 'border-stone-300'}`}>{selected && <div className="w-2 h-2 rounded-full bg-white" />}</div>
              {option}
            </div>
          </button>
        );
      })}
    </div>
  );
}

export function ShortAnswerInput({ value, onChange, questionIdx }) {
  const words = (value || '').trim().split(/\s+/).filter(Boolean).length;
  return (
    <div data-testid={`short-answer-question-${questionIdx}`}>
      <p className="text-xs text-stone-500 mb-2 font-medium">Explain in your own words — graded by AI against the training document</p>
      <Textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={4} placeholder="Type your answer here (2–4 sentences)…"
        className="rounded-xl text-sm" data-testid={`short-answer-input-${questionIdx}`} />
      <p className="text-[11px] text-stone-400 mt-1 text-right">{words} word{words !== 1 ? 's' : ''}</p>
    </div>
  );
}

export function ShortAnswerReview({ item }) {
  const score = item.ai_score ?? 0;
  const tone = score >= 70 ? 'green' : score >= 40 ? 'amber' : 'red';
  const badgeCls = { green: 'bg-green-100 text-green-700', amber: 'bg-amber-100 text-amber-700', red: 'bg-red-100 text-red-700' }[tone];
  return (
    <div className="ml-7 space-y-2" data-testid={`short-answer-review-${item.index}`}>
      <div className="flex items-center gap-2"><Badge className={`${badgeCls} text-[10px]`}>AI score {score}%</Badge></div>
      <div className="rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm"><p className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold mb-0.5">Your answer</p><p className="text-stone-700 whitespace-pre-line">{item.user_answer || '—'}</p></div>
      {item.feedback && <div className={`rounded-xl border px-3 py-2 text-sm flex items-start gap-2 ${tone === 'green' ? 'border-green-200 bg-green-50 text-green-900' : tone === 'amber' ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-red-200 bg-red-50 text-red-900'}`}><Lightbulb className="w-4 h-4 shrink-0 mt-0.5" /><span>{item.feedback}</span></div>}
      {item.model_answer && <div className="rounded-xl border border-stone-100 bg-stone-50 px-3 py-2 text-sm"><p className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold mb-0.5">Model answer</p><p className="text-stone-600">{item.model_answer}</p></div>}
    </div>
  );
}

export function isAnswered(q, a) {
  if (a === undefined || a === null) return false;
  if (q?.type === 'short_answer') return typeof a === 'string' && a.trim().length > 0;
  if (Array.isArray(a)) return a.length > 0;
  return true;
}
